import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar
} from "@mui/material";
import MDEditor from "@uiw/react-md-editor";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import api from "../../api";

// Sortable Step Component
const SortableStep = ({ step, stepIndex, steps, removeStep, updateStepContent, fileInputRefs, handleImageUpload, imageMap, removeImage, getImageTag, copyImageTag, copiedIndex }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `step-${stepIndex}` });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      elevation={isDragging ? 6 : 2}
      sx={{
        p: 3,
        mb: 3,
        position: "relative",
        border: "1px solid #e0e0e0",
        ...style
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <div {...attributes} {...listeners}>
            <DragIndicatorIcon sx={{ mr: 1, cursor: "grab" }} />
          </div>
          <Typography variant="h6">Step {stepIndex + 1}</Typography>
        </Box>
        {steps.length > 1 && (
          <IconButton
            color="error"
            onClick={() => removeStep(stepIndex)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      <MDEditor
        value={step.content}
        onChange={(content) => updateStepContent(stepIndex, content)}
        height={200}
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Images for this step
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => fileInputRefs.current[stepIndex]?.click()}
          >
            Upload Image
          </Button>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={el => fileInputRefs.current[stepIndex] = el}
            onChange={(e) => handleImageUpload(stepIndex, e)}
          />
        </Box>

        {step.imageRefs.length > 0 && (
          <List sx={{ mt: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            {step.imageRefs.map((imageRef) => {
              const imageData = imageMap.get(imageRef);
              if (!imageData) return null;

              return (
                <ListItem key={imageRef} divider>
                  <Box
                    component="img"
                    src={imageData.url}
                    alt={imageData.name}
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      mr: 2,
                      borderRadius: 1,
                    }}
                  />
                  <ListItemText
                    primary={imageData.name}
                    secondary={
                      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                        <TextField
                          size="small"
                          value={getImageTag(imageRef)}
                          variant="outlined"
                          InputProps={{ readOnly: true }}
                          sx={{ mr: 1 }}
                        />
                        <IconButton
                          onClick={() => copyImageTag(imageRef)}
                          color={copiedIndex === imageRef ? "success" : "default"}
                          size="small"
                        >
                          {copiedIndex === imageRef ? (
                            <CheckCircleIcon />
                          ) : (
                            <ContentCopyIcon />
                          )}
                        </IconButton>
                        {copiedIndex === imageRef && (
                          <Typography
                            variant="caption"
                            sx={{ color: "green", ml: 1 }}
                          >
                            Copied!
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => removeImage(stepIndex, imageRef)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
};

const MultiStepCard = ({ token }) => {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const fileInputRefs = useRef([]);

  // State for the card
  const [title, setTitle] = useState("");
  const [answer, setAnswer] = useState("");
  const [steps, setSteps] = useState([{ content: "", imageRefs: [] }]);
  const [status, setStatus] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [activeId, setActiveId] = useState(null);

  // Configure DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Map to store image data
  const [imageMap, setImageMap] = useState(new Map());

  // Function to add a step at specific index
  const addStepAtIndex = (index) => {
    const newSteps = [...steps];
    newSteps.splice(index, 0, { content: "", imageRefs: [] });
    setSteps(newSteps);
    
    // Expand the refs array to match step count
    fileInputRefs.current = [
      ...fileInputRefs.current.slice(0, index),
      null,
      ...fileInputRefs.current.slice(index)
    ];
  };

  // Add a new step at the end
  const addStep = () => {
    setSteps([...steps, { content: "", imageRefs: [] }]);
    // Expand the refs array to match step count
    fileInputRefs.current = [...fileInputRefs.current, null];
  };

  // Remove a step
  const removeStep = (index) => {
    // Get image refs from this step to remove from map
    const imageRefsToRemove = steps[index].imageRefs;

    // Update steps
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);

    // Remove image refs from map
    const newImageMap = new Map(imageMap);
    imageRefsToRemove.forEach(ref => {
      newImageMap.delete(ref);
    });
    setImageMap(newImageMap);

    // Update refs array
    fileInputRefs.current.splice(index, 1);
  };

  // Update step content
  const updateStepContent = (index, content) => {
    const newSteps = [...steps];
    newSteps[index].content = content;
    setSteps(newSteps);
  };

  // Handle reordering steps
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const activeIndex = parseInt(active.id.split('-')[1]);
      const overIndex = parseInt(over.id.split('-')[1]);
      
      setSteps((items) => {
        const newItems = arrayMove(items, activeIndex, overIndex);
        
        // Reorder the file input refs as well
        const newRefs = [...fileInputRefs.current];
        fileInputRefs.current = arrayMove(newRefs, activeIndex, overIndex);
        
        return newItems;
      });
    }
    
    setActiveId(null);
  };

  // Handle image upload for a specific step
  const handleImageUpload = (stepIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Generate a unique image reference ID
    const imageRef = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create a temporary URL for preview only
    const imageUrl = URL.createObjectURL(file);

    // Store image file and metadata in the map
    const newImageMap = new Map(imageMap);
    newImageMap.set(imageRef, {
      file,
      name: file.name,
      url: imageUrl
    });
    setImageMap(newImageMap);

    // Add the image reference to the step
    const newSteps = [...steps];
    newSteps[stepIndex].imageRefs.push(imageRef);
    setSteps(newSteps);

    // Reset the file input
    e.target.value = "";
  };

  // Remove an image from a step
  const removeImage = (stepIndex, imageRef) => {
    // Remove the image reference from the step
    const newSteps = [...steps];
    newSteps[stepIndex].imageRefs = newSteps[stepIndex].imageRefs.filter(ref => ref !== imageRef);
    setSteps(newSteps);

    // Remove the image from the map
    const newImageMap = new Map(imageMap);

    // Revoke the object URL to prevent memory leaks
    if (newImageMap.has(imageRef)) {
      URL.revokeObjectURL(newImageMap.get(imageRef).url);
    }

    newImageMap.delete(imageRef);
    setImageMap(newImageMap);
  };

  // Generate image tag for an image reference
  const getImageTag = (imageRef) => {
    return `[IMAGE:${imageRef}]`;
  };

  // Copy image tag to clipboard
  const copyImageTag = (imageRef) => {
    const tag = getImageTag(imageRef);
    navigator.clipboard.writeText(tag);

    // Show "Copied!" status
    setCopiedIndex(imageRef);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Reset form after successful submission
  const resetForm = () => {
    setTitle("");
    setAnswer("");
    setSteps([{ content: "", imageRefs: [] }]);
    
    // Clear image map and revoke all object URLs
    imageMap.forEach(imageData => {
      URL.revokeObjectURL(imageData.url);
    });
    setImageMap(new Map());
    
    // Reset file input refs
    fileInputRefs.current = [null];
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || steps.some(step => !step.content) || !answer) {
      setStatus({ type: "error", message: "Title, all steps, and answer are required!" });
      return;
    }

    try {
      const formData = new FormData();

      // Standard fields
      formData.append("title", title);
      formData.append("answer", answer);

      // Attach all images
      for (const [imageRef, imageData] of imageMap.entries()) {
        formData.append(`images[${imageRef}]`, imageData.file);
      }

      // Send the entire steps array as one JSON string
      // Each step has { content, imageRefs }, etc.
      formData.append("stepsJson", JSON.stringify(steps));

      const response = await api.post(
        `/decks/${deckId}/add-multi-step-card`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 201) {
        // Show success snackbar
        setSnackbar({
          open: true,
          message: "Card created successfully!",
          severity: "success"
        });
        
        // Reset the form
        resetForm();
      } else {
        // Show error snackbar
        setSnackbar({
          open: true,
          message: "Error creating card. Please try again.",
          severity: "error"
        });
      }
    } catch (error) {
      console.error(error);
      // Show error snackbar
      setSnackbar({
        open: true,
        message: "Error creating card: " + (error.response?.data?.message || error.message),
        severity: "error"
      });
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      imageMap.forEach(imageData => {
        URL.revokeObjectURL(imageData.url);
      });
    };
  }, []);

  // Initialize refs array for file inputs
  useEffect(() => {
    fileInputRefs.current = Array(steps.length).fill(null);
  }, []);

  return (
    <Container sx={{ maxWidth: "80%", mt: 5 }}>
      <Typography variant="h4" gutterBottom>
        Create Multi-Step Card
      </Typography>

      {status && (
        <Alert
          severity={status.type}
          sx={{ mb: 2 }}
          onClose={() => setStatus(null)}
        >
          {status.message}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Card Title"
          variant="outlined"
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Box>

      {/* Add step button at the top */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => addStepAtIndex(0)}
        >
          Add Step
        </Button>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={steps.map((_, index) => `step-${index}`)}
          strategy={verticalListSortingStrategy}
        >
          {steps.map((step, stepIndex) => (
            <React.Fragment key={`step-group-${stepIndex}`}>
              <SortableStep
                step={step}
                stepIndex={stepIndex}
                steps={steps}
                removeStep={removeStep}
                updateStepContent={updateStepContent}
                fileInputRefs={fileInputRefs}
                handleImageUpload={handleImageUpload}
                imageMap={imageMap}
                removeImage={removeImage}
                getImageTag={getImageTag}
                copyImageTag={copyImageTag}
                copiedIndex={copiedIndex}
              />
              
              {/* Add step button between cards */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addStepAtIndex(stepIndex + 1)}
                  size="small"
                >
                  Add Step
                </Button>
              </Box>
            </React.Fragment>
          ))}
        </SortableContext>
        
        {/* Drag overlay for dragging feedback */}
        <DragOverlay>
          {activeId ? (
            <Paper 
              elevation={6}
              sx={{
                p: 3,
                opacity: 0.8,
                border: "1px solid #e0e0e0",
                width: "100%"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DragIndicatorIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Step {parseInt(activeId.split('-')[1]) + 1}
                </Typography>
              </Box>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Answer
        </Typography>
        <MDEditor
          value={answer}
          onChange={setAnswer}
          height={200}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 3, mb: 5 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Save Card
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(`/decks/${deckId}`)}
        >
          Cancel
        </Button>
      </Box>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MultiStepCard;